// Marketing sections part 2: Industries, Social Proof, Integrations + Pricing,
// Why Fieldlink, FAQ, Final CTA and Footer.
import { Link } from "react-router-dom";
import { ArrowRight, Check, Quote, ChevronDown, Mountain, Droplets, Route, Truck, HardHat, Plug } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import {
  INDUSTRIES, METRICS, METRICS_FOOTNOTE, TESTIMONIALS, INTEGRATIONS, PLANS,
  PRODUCT_MENU, PRICING_BENEFITS, WHY_POINTS, TRIAL_CTA, SUPPORT_EMAIL, HOSTING_CLAIM,
} from "@/data/marketing";
import { Reveal } from "./MarketingSections";
import "./website.css";

const INDUSTRY_ICONS = [Mountain, Droplets, Route, Truck, HardHat];

/* ------------------------------ INDUSTRIES ------------------------------ */
export function IndustryCards() {
  return (
    <section id="industries" className="bg-[#07090D] text-white py-16 lg:py-24 border-t border-white/[0.06]">
      <div className="max-w-[1520px] mx-auto px-5 lg:px-8">
        <Reveal className="text-center">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight">Built for the work you actually do</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {INDUSTRIES.map((ind, i) => {
            const Icon = INDUSTRY_ICONS[i] || HardHat;
            return (
              <Reveal key={ind.t} delay={i * 60}>
                <div className="group relative rounded-lg overflow-hidden border border-white/10 aspect-[4/5]" data-testid={`industry-${ind.t.toLowerCase().replace(/\s+/g, "-")}`}>
                  <img src={ind.img} alt={`${ind.t} — civil construction`} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                  <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(7,9,13,0.95) 15%, rgba(7,9,13,0.35) 55%, rgba(7,9,13,0.1) 100%)" }} />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <span className="w-9 h-9 rounded-md bg-[#FF5F15] flex items-center justify-center mb-3"><Icon className="w-4.5 h-4.5 text-white" style={{ width: 18, height: 18 }} /></span>
                    <h3 className="font-display font-black uppercase tracking-tight text-lg">{ind.t}</h3>
                    <p className="text-[12.5px] text-zinc-300 mt-1 leading-snug">{ind.d}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ SOCIAL PROOF ------------------------------ */
export function SocialProof() {
  if (!TESTIMONIALS.length && !METRICS.length) return null;
  return (
    <section className="bg-[#0A0E14] text-white py-16 lg:py-20 border-t border-white/[0.06]">
      <div className="max-w-[1520px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-8 items-stretch">
        <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-4">
          {METRICS.map((m) => (
            <div key={m.label} className="rounded-lg border border-white/10 bg-white/[0.02] px-5 py-5 lg:py-6" data-testid={`metric-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="font-display font-black text-3xl lg:text-4xl text-[#FF5F15]">{m.value}</div>
              <div className="text-[12px] text-zinc-400 mt-1.5">{m.label}</div>
            </div>
          ))}
          <p className="col-span-3 lg:col-span-1 text-[10px] text-zinc-600">*{METRICS_FOOTNOTE}</p>
        </div>
        <div className="lg:col-span-8">
          {TESTIMONIALS.length > 0 ? (
            <div className="h-full rounded-lg border border-white/10 bg-[#0B1018] p-8 lg:p-12 flex flex-col justify-center">
              <Quote className="w-10 h-10 text-[#FF5F15]" />
              <blockquote className="mt-5 text-xl lg:text-2xl font-semibold leading-relaxed">{TESTIMONIALS[0].quote}</blockquote>
              <div className="mt-6">
                <div className="font-bold">{TESTIMONIALS[0].name}</div>
                <div className="text-sm text-zinc-500">{TESTIMONIALS[0].role} — {TESTIMONIALS[0].company}</div>
              </div>
            </div>
          ) : (
            <div className="h-full rounded-lg border border-white/10 bg-[#0B1018] p-8 lg:p-12 flex flex-col justify-center" data-testid="proof-placeholder">
              <Quote className="w-10 h-10 text-[#FF5F15]" />
              <p className="mt-5 text-xl lg:text-2xl font-semibold leading-relaxed text-zinc-200">
                Fieldlink is run day-in, day-out by NZ civil crews — from owner-operators to multi-site contractors.
              </p>
              <p className="mt-4 text-sm text-zinc-500">Want to hear how a crew like yours runs Fieldlink? We'll put you in touch.</p>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Talk%20to%20a%20Fieldlink%20crew`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#FF5F15] hover:text-[#ff7a3d] transition-colors duration-150 w-fit">
                Talk to us <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* -------------------------- INTEGRATIONS + PRICING -------------------------- */
export function IntegrationsPricing() {
  return (
    <section id="pricing" className="bg-[#07090D] text-white py-16 lg:py-24 border-t border-white/[0.06]">
      <div className="max-w-[1520px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
        {/* integrations */}
        <div className="lg:col-span-3">
          <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tight">Works with the tools you use.</h2>
          <p className="mt-3 text-sm text-zinc-400">Your accounts stay where they live — Fieldlink feeds them.</p>
          <div className="mt-6 space-y-3">
            {INTEGRATIONS.map((it) => (
              <div key={it.name} className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3.5" data-testid={`integration-${it.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="w-9 h-9 rounded-md border border-[#FF5F15]/50 flex items-center justify-center shrink-0"><Plug className="w-4 h-4 text-[#FF5F15]" /></span>
                <div><div className="text-sm font-bold">{it.name}</div><div className="text-[12px] text-zinc-500 mt-0.5">{it.d}</div></div>
              </div>
            ))}
          </div>
        </div>

        {/* pricing */}
        <div className="lg:col-span-9">
          <div className="text-center lg:text-left">
            <h2 className="font-display font-black text-3xl lg:text-4xl uppercase tracking-tight">Simple, flat pricing. <span className="text-[#FF5F15]">All modules included.</span></h2>
            <p className="mt-2 text-sm text-zinc-400">NZD per month, whole crew included. 7-day free trial on every plan.</p>
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-4 items-stretch">
            {PLANS.map((p) => (
              <div key={p.name} data-testid={`plan-${p.name.toLowerCase()}`}
                className={`relative rounded-lg p-6 flex flex-col ${p.highlight ? "border-2 border-[#FF5F15] bg-[#120B06] shadow-[0_0_50px_rgba(255,95,21,0.12)]" : "border border-white/12 bg-white/[0.02]"}`}>
                {p.highlight && <span className="absolute -top-3 left-5 bg-[#FF5F15] text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-sm">Site plan</span>}
                <div className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-[#FF5F15]">{p.seats}</div>
                <h3 className="font-display font-black uppercase tracking-tight text-2xl mt-1">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-display font-black text-4xl">${p.price}</span>
                  <span className="text-[11px] font-mono uppercase text-zinc-500">NZD/mo</span>
                </div>
                <p className="text-[13px] text-zinc-400 mt-2 flex-1">{p.desc}</p>
                <Link to="/register" data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  className={`mt-5 inline-flex items-center justify-center h-11 rounded-md text-[13px] font-bold transition-colors duration-150 ${p.highlight ? "bg-[#FF5F15] hover:bg-[#E65100] text-white" : "border border-white/20 hover:bg-white hover:text-black text-white"}`}>
                  Start free trial
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            {PRICING_BENEFITS.map((b) => (
              <span key={b} className="flex items-center gap-1.5 text-[12.5px] text-zinc-400"><Check className="w-3.5 h-3.5 text-[#FF5F15]" /> {b}</span>
            ))}
            <Link to="/pricing" className="text-[12.5px] font-bold text-[#FF5F15] hover:text-[#ff7a3d] ml-auto" data-testid="pricing-full-link">
              Full pricing & comparison →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- WHY FIELDLINK -------------------------------- */
export function WhyFieldlink() {
  return (
    <section id="why" className="bg-[#0A0E14] text-white py-16 lg:py-24 border-t border-white/[0.06]">
      <div className="max-w-[1520px] mx-auto px-5 lg:px-8">
        <Reveal><h2 className="font-display font-black text-3xl lg:text-4xl uppercase tracking-tight text-center">Built differently for civil.</h2></Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {WHY_POINTS.map((w, i) => (
            <Reveal key={w.t} delay={i * 50}>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-6 h-full hover:border-[#FF5F15]/40 transition-colors duration-200">
                <span className="w-9 h-9 rounded-md bg-[#FF5F15]/12 border border-[#FF5F15]/40 flex items-center justify-center"><Check className="w-4 h-4 text-[#FF5F15]" /></span>
                <h3 className="mt-4 font-display font-black uppercase tracking-tight text-base">{w.t}</h3>
                <p className="mt-1.5 text-[13px] text-zinc-400 leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------ FAQ ------------------------------------ */
const FAQS = [
  { q: "Does it work with no signal on site?", a: "Yeah — the field app is an offline-capable PWA. Crews keep clocking hours, ticking pre-starts and snapping photos; it all syncs when they're back in coverage." },
  { q: "Does it talk to Xero?", a: "Customer invoices push to Xero as drafts, so your accounts stay in one place. Payroll exports come out of the timesheet approvals." },
  { q: "How long does setup take?", a: "Most crews are running in an afternoon. Load your people, fleet and jobs — there's no consultant or setup fee." },
  { q: "What do the field crew need?", a: "Just a phone. It installs like an app from the browser — no app store, no updates to chase." },
  { q: "Is our data held in NZ?", a: `Fieldlink is ${HOSTING_CLAIM} and built for NZ contractors, with your company's data kept separate from everyone else's.` },
  { q: "Can we cancel?", a: "Any time. Start with the 7-day free trial — no credit card needed to kick off." },
];
export function FaqSection() {
  return (
    <section id="faq" className="bg-[#07090D] text-white py-16 lg:py-20 border-t border-white/[0.06]">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <h2 className="font-display font-black text-3xl uppercase tracking-tight text-center">Fair questions</h2>
        <div className="mt-8 space-y-2.5">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 font-bold text-sm min-h-[44px]">
                {f.q}
                <ChevronDown className="w-4 h-4 shrink-0 text-[#FF5F15] group-open:rotate-180 transition-transform duration-150" />
              </summary>
              <div className="px-5 pb-4 text-sm text-zinc-400 leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- FINAL CTA --------------------------------- */
export function FinalCTA() {
  return (
    <section className="relative bg-[#07090D] text-white overflow-hidden border-t border-white/[0.06]">
      <img src="/footer-bg.jpg" alt="" aria-hidden loading="lazy" className="absolute inset-0 w-full h-full object-cover object-center opacity-50" />
      <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(7,9,13,0.95) 0%, rgba(7,9,13,0.7) 50%, rgba(7,9,13,0.96) 100%)" }} />
      <div className="relative max-w-[1520px] mx-auto px-5 lg:px-8 py-20 lg:py-28 text-center">
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
          Less paperwork.<br /><span className="text-[#FF5F15]">More mahi.</span>
        </h2>
        <p className="mt-5 text-zinc-300 text-base lg:text-lg">Run your civil business from one connected platform.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/register" data-testid="final-cta-start"
            className="inline-flex items-center h-12 px-7 rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold text-sm transition-colors duration-150 shadow-[0_0_40px_rgba(255,95,21,0.35)]">
            {TRIAL_CTA} <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
          <a href={`mailto:${SUPPORT_EMAIL}?subject=Fieldlink%20demo%20request`} data-testid="final-cta-demo"
            className="inline-flex items-center h-12 px-7 rounded-md border border-white/30 bg-white/[0.04] hover:bg-white/10 text-white font-bold text-sm transition-colors duration-150">
            Book a demo
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- FOOTER ---------------------------------- */
const FOOTER_COLS = [
  { h: "Product", links: [["Features", "/features"], ["Pricing", "/pricing"], ["Mobile App", "/download"], ["Xero Integration", "/xero-timesheet-integration-nz"], ["Fieldlink vs Simpro", "/simpro-alternative-nz"]] },
  { h: "Solutions", links: [["Civil Contractors", "/civil-construction-software-nz"], ["Timesheets", "/timesheet-app-nz"], ["Safety Management", "/safety-management-nz"], ["Fleet Management", "/fleet-management-nz"], ["Payroll", "/payroll-software-civil-nz"]] },
  { h: "Resources", links: [["Blog", "/blog"], ["FAQ", "/faq"], ["SSSP Generator", "/sssp-generator-nz"], ["Pre-Start Checklists", "/pre-start-checklist-app-nz"], ["Job Management", "/job-management-nz"]] },
  { h: "Company", links: [["Security", "/security"], ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Sign in", "/login"]] },
];
export function MarketingFooter() {
 return <footer className="web-footer"><div className="web-wrap"><div className="web-footer-grid"><div><Link to="/" className="web-brand"><BrandMark variant="mark" size={30}/><span>FIELDLINK<b>NZ</b></span></Link><p>Civil construction software built in New Zealand. From tender to timesheet. Site to invoice.</p><a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></div>{FOOTER_COLS.map(c=><div key={c.h}><h2>{c.h}</h2><ul>{c.links.map(([text,to])=><li key={to}><Link to={to}>{text}</Link></li>)}</ul></div>)}</div><details><summary>More civil construction tools and resources</summary><ul>{[["Civil estimating", "/civil-estimating-software-nz"], ["Construction takeoffs", "/construction-takeoff-software-nz"], ["Job costing", "/civil-job-costing-software-nz"], ["Crew scheduling", "/civil-crew-scheduling-software-nz"], ["Construction QA", "/construction-qa-software-nz"], ...PRODUCT_MENU.flatMap(g=>g.items)].filter((item,i,all)=>all.findIndex(other=>other[0]===item[0])===i).map(([text,to])=><li key={text}>{to.startsWith("/#")?<a href={to}>{text}</a>:<Link to={to}>{text}</Link>}</li>)}</ul></details><div className="web-footer-bottom"><span>© {new Date().getFullYear()} FieldlinkNZ · Built in Aotearoa for civil crews</span><span><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/security">Security</Link></span></div></div></footer>;
}
